import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Input,
  Spinner,
  Pagination,
  Tabs,
  Tab,
  Divider,
  User,
  Chip,
} from "@nextui-org/react";
import { FiSearch } from "react-icons/fi";
import { FaUser } from "react-icons/fa6";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { changeStatus } from "../store/slices/supplierSlice";
import { STATUS } from "../store/constants";
import { useNavigate } from "react-router-dom";
import { displayDate, formatTime } from "../utils";
import { refreshIfNeeded } from "../store/actions/refreshIfNeeded";
import { removeAuth } from "../store/slices/authSlice";
import { toast } from "react-toastify";
 
const BIG_LIMIT = 10000; // enough to grab everything in one call
 const getColumns = (statusFilter) => [
  { uid: "profile", name: "Profile" },
  { uid: "name", name: "Name" },
  { uid: "email", name: "Email" },
  { uid: "phone", name: "Phone No" },
  {
    uid: "companyOrBusiness",
    name: statusFilter === "SUPPLIER" ? "Company Name" : "Business Name",
  },
  { uid: "requestedDate", name: "Requested Date" },
];
const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = localStorage.getItem("accessToken");
  const { status } = useSelector((s) => s.supplier);

  // ── state ──────────────────────────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState("CUSTOMER");
  const [filterValue, setFilterValue] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
   const [sortDescriptor, setSortDescriptor] = useState({
        column: null,
        direction: null, 
    });


 
const [totalPages, setTotalPages] = useState(1);
const [allDataLoaded, setAllDataLoaded] = useState(false);
const [batchPage, setBatchPage] = useState(1);
const batchSize = 100; // each batch = 100 users from API
const debounceRef = React.useRef(null);
const [totalRe  , setTotalRe] = useState(1);

  const ensureAuth = async () => {
    await dispatch(refreshIfNeeded());
    const token = localStorage.getItem("accessToken");
    return {
      headers: { Authorization: `Bearer ${token}` },
    };
  };
 
const INITIAL_VISIBLE_COLUMNS = getColumns(statusFilter).map((col) => col.uid);

  // 1. Sorting logic
  
  // ── fetch all users/disputes for current tab ───────────────────────────
const getDetails = useCallback(async (pageToLoad = 1, opts = {}) => {
  dispatch(changeStatus(STATUS.LOADING));
  await dispatch(refreshIfNeeded());

  const fresh = localStorage.getItem("accessToken");
  const auth = { headers: { Authorization: `Bearer ${fresh}` } };

  const qp = new URLSearchParams();
  qp.append("page", pageToLoad);
  qp.append("pageSize", batchSize);

  if (opts.searchParam || filterValue)
    qp.append("search", opts.searchParam ?? filterValue);

  if (sortDescriptor.column) {
    qp.append("sortBy", sortDescriptor.column);
    qp.append(
      "sortOrder",
      sortDescriptor.direction === "ascending" ? "asc" : "desc"
    );
  }

  const urlBase = `${process.env.REACT_APP_SERVER_URL}/admin/`;
  const url =
    statusFilter !== "disputes"
      ? `${urlBase}getAlluser?role=${statusFilter}&${qp.toString()}`
      : `${urlBase}disputelist?${qp.toString()}`;

  try {
    const res = await axios.get(url, auth);

    // Pull raw list (may be array, object, undefined, etc.)
    const rawList =
      statusFilter !== "disputes"
        ? res?.data?.details?.users
        : res?.data?.details?.disputes;

    // Defensive normalization to an array
    let list;
    if (Array.isArray(rawList)) {
      list = rawList;
    } else if (rawList && typeof rawList === "object") {
      // If it's an object with numeric keys or nested structure, try to extract values.
      // This covers cases like { "0": {...}, "1": {...} } or { rows: [...] }
      if (Array.isArray(rawList.rows)) {
        list = rawList.rows;
      } else {
        list = Object.values(rawList);
      }
    } else {
      list = [];
    }

    // If nothing sensible found, log the response (helps debugging)
    if (!list.length && rawList && !Array.isArray(rawList)) {
      // optional: remove or change to debug level later
      // eslint-disable-next-line no-console
      console.warn("Unexpected list shape from API:", rawList, res?.data?.details);
    }

    // batch-wise append or replace
    if (opts.replace) {
      setData(list);
      setAllDataLoaded(list.length < batchSize);
      setBatchPage(1);
    } else {
      setData((prev) => [...prev, ...list]);
      setAllDataLoaded(list.length < batchSize);
      setBatchPage(pageToLoad);
    }

    // Determine totalRecords defensively
    const total =
      res?.data?.details?.index?.totalRecords ??
      res?.data?.details?.totalRecords ??
      // fallback to whatever length we can trust
      (Array.isArray(list) ? list.length : 0);

    setTotalRe(total);
    setTotalPages(Math.ceil(total / rowsPerPage));
  } catch (err) {
    console.error(err);
    const code = err?.response?.status;
    if (code === 401) {
      toast.error("Session expired. Please log in again.");
      localStorage.clear();
      dispatch(removeAuth());
      navigate("/login");
    }
    dispatch(changeStatus(STATUS.ERROR));
  } finally {
    dispatch(changeStatus(STATUS.IDLE));
  }
}, [dispatch, navigate, statusFilter, sortDescriptor, filterValue, rowsPerPage, batchSize]);


useEffect(() => {
  // Reset pagination and data
  setData([]);
  setBatchPage(1);
  setAllDataLoaded(false);

  // Fetch first page for new role
  getDetails(1, { replace: true });
}, [statusFilter , sortDescriptor]); // Run whenever statusFilter changes

 
  // reset to first page on new search text
  useEffect(() => setPage(1), [filterValue]);


  useEffect(() => {
  const requiredEndIndex = page * rowsPerPage;
  if (requiredEndIndex <= data.length) return;
  if (allDataLoaded) return;

  const requiredBatch = Math.ceil(requiredEndIndex / batchSize);
  for (let b = batchPage + 1; b <= requiredBatch; b++) {
    getDetails(b);
  }
}, [page, rowsPerPage, data.length, allDataLoaded, batchPage, getDetails]);

  // ── filtering & pagination (client-side only) ──────────────────────────
  const filteredItems = useMemo(() => {
    if (!filterValue) return data;
    const q = filterValue.toLowerCase();

    return data.filter((it) => {
      if (statusFilter !== "disputes") {
        const first = it.first_name?.toLowerCase() ?? "";
        const last = it.last_name?.toLowerCase() ?? "";
        const em = it.email?.toLowerCase() ?? "";
        return first.includes(q) || last.includes(q) || em.startsWith(q);
      } else {
        const make = it.Order?.Request?.VehicleMake?.name?.toLowerCase() ?? "";
        const model =
          it.Order?.Request?.VehicleModel?.name?.toLowerCase() ?? "";
        const part = it.Order?.Request?.Part?.name?.toLowerCase() ?? "";
        return make.includes(q) || model.includes(q) || part.includes(q);
      }
    });
  }, [filterValue, data, statusFilter]);

  const totalPagess = Math.max(1, Math.ceil( totalRe / rowsPerPage));

  const paginatedItems = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  const getSortValue = (item, column) => {
  switch (column) {
    case "profile":
      return item?.profile_id || "";

    case "name":
      return `${item?.first_name || ""} ${item?.last_name || ""}`.trim().toLowerCase();

    case "email":
      return (item?.email || item?.User?.email || "").toLowerCase();

    case "phone":
      return (item?.phone || item?.User?.phone || "").toLowerCase();

    case "companyOrBusiness":
      return (
        item?.business_name ||
        item?.company_name ||
        item?.User?.business_name ||
        item?.User?.company_name ||
        ""
      ).toLowerCase();

    case "requestedDate":
      return (
        new Date(item?.createdAt || item?.Request?.date_requested || 0).getTime() ||
        0
      );

    default:
      return (item?.[column] || "").toString().toLowerCase();
  }
};

 const sortedItems = useMemo(() => {
    if (!sortDescriptor.column) return filteredItems;

    return [...filteredItems].sort((a, b) => {
      let v1 = getSortValue(a, sortDescriptor.column);
      let v2 = getSortValue(b, sortDescriptor.column);

      // Detect date
      const isDate1 = !isNaN(Date.parse(v1));
      const isDate2 = !isNaN(Date.parse(v2));

      if (isDate1 && isDate2) {
        v1 = new Date(v1).getTime();
        v2 = new Date(v2).getTime();
      }

      // Detect numbers
      const isNum1 = !isNaN(v1) && v1 !== "";
      const isNum2 = !isNaN(v2) && v2 !== "";

      if (isNum1 && isNum2) {
        const result =
          sortDescriptor.direction === "ascending" ? v1 - v2 : v2 - v1;
        return result;
      }

      // Default string compare
      v1 = v1.toString().toLowerCase();
      v2 = v2.toString().toLowerCase();

      const cmp = v1.localeCompare(v2);

      return sortDescriptor.direction === "ascending" ? cmp : -cmp;
    });
  }, [filteredItems, sortDescriptor]);



    // 2. Pagination applied AFTER sorting
    const pagedItems = useMemo(() => {
      const start = (page - 1) * rowsPerPage;
      return sortedItems.slice(start, start + rowsPerPage);
    }, [sortedItems, page, rowsPerPage]);
  // ── handlers ───────────────────────────────────────────────────────────
 const onSearchChange = useCallback((val) => {
  setFilterValue(val);

  if (debounceRef.current) clearTimeout(debounceRef.current);

  debounceRef.current = setTimeout(() => {
    getDetails(1, { replace: true, searchParam: val });
  }, 500);
}, [getDetails]);

useEffect(() => () => {
  if (debounceRef.current) clearTimeout(debounceRef.current);
}, []);

  const onClear = useCallback(() => setFilterValue(""), []);
  const onRowsPerPageChange = useCallback((e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  }, []);
  const userDetails = (id) =>
    navigate(
      statusFilter !== "disputes" ? `/updateprofile/${id}` : `/disputes/${id}`
    );

  // ── render ────────────────\─────────────────────────────────────────────
  return (
    <div>
      <Table
        aria-label="User Table"
        isStriped
        isHeaderSticky
        sortDescriptor={sortDescriptor}
        onSortChange={setSortDescriptor}
        topContent={
          <div className="flex flex-col gap-6 select-none">
            {/* search bar */}
            <div className="flex justify-between items-center gap-3">
              <Input
                isClearable
                placeholder="Search Users..."
                startContent={<FiSearch />}
                value={filterValue}
                onClear={onClear}
                onValueChange={onSearchChange}
                className="w-full sm:max-w-[44%]"
              />
            </div>

            {/* tabs + paginator controls */}
            <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-y-4">
              <Tabs
                aria-label="Options"
                variant="underlined"
                selectedKey={statusFilter}
                onSelectionChange={(k) => setStatusFilter(k)}
              >
                <Tab key="CUSTOMER" title="All Customer" />
                <Tab key="SUPPLIER" title="All Supplier" />
                <Tab key="disputes" title="All Disputes" />
              </Tabs>

              <div className="flex items-center gap-5 text-xs sm:text-sm text-default-400 capitalize">
                <span>
                  Total: { totalRe}{" "}
                  {statusFilter !== "disputes" ? "users" : "disputes"}
                </span>
                <Divider orientation="vertical" className="h-6" />
                <label className="flex items-center">
                  Rows per page:
                  <select
                    className="bg-transparent outline-none ml-1"
                    value={rowsPerPage}
                    onChange={onRowsPerPageChange}
                  >
                    {[6, 10, 15].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          </div>
        }
        bottomContent={
          <div className="py-2 px-2 flex justify-center">
            <Pagination
              isCompact
              showControls
              showShadow
              color="primary"
              page={page}
              total={totalPagess}
              onChange={(p) => setPage(p)}
            />
          </div>
        }
      > 
         <TableHeader>
  {getColumns(statusFilter).map((col) => (
    <TableColumn key={col.uid}     allowsSorting={
  col.uid === "name" ||
  col.uid === "companyOrBusiness" ||
  col.uid === "requestedDate"
}>{col.name}</TableColumn>
  ))}
</TableHeader>
        <TableBody
          items={paginatedItems}
          emptyContent={
            status === STATUS.LOADING
              ? null
              : filteredItems.length
              ? "No matching users"
              : statusFilter !== "disputes"
              ? "No User found"
              : "No Dispute found"
          }
          isLoading={status === STATUS.LOADING && data.length === 0}
          loadingContent={<Spinner label="Loading..." className="w-full" />}
        >
          {(item) => {
            const makeName =
              item.Order?.Request?.VehicleMake?.name ??
              item.Order?.Request?.custom_make ??
              "";
            const modelName =
              item.Order?.Request?.VehicleModel?.name ??
              item.Order?.Request?.custom_model ??
              "";

            return (
              <TableRow
                key={item.id}
                onClick={() => userDetails(item.id)}
                className="cursor-pointer min-h-[56px]"
              >
                <TableCell className="capitalize py-3 px-2">
                  {statusFilter !== "disputes" ? (
                    item.profile_id ? (
                      <img
                        src={item.profile_id}
                        className="w-10 h-10 rounded-full"
                        alt="profile"
                      />
                    ) : (
                      <User
                        as="button"
                        avatarProps={{
                          showFallback: true,
                          fallback: <FaUser size={18} color="gray" />,
                        }}
                      />
                    )
                  ) : (
                    <Chip
                      variant="flat"
                      size="sm"
                      className="capitalize px-2 py-4 text-xs 
                      "
                    >
                      {item.User?.role ?? ""}
                    </Chip>
                  )}
                </TableCell>

                <TableCell className="capitalize py-3 px-2">
                  {statusFilter !== "disputes"
                    ? `${item.first_name || ""} ${item.last_name || ""}`.trim()
                    : `${makeName} ${modelName}`.trim()}
                </TableCell>

                {/* <TableCell className="capitalize py-3 px-2">
                  {statusFilter !== "disputes"
                    ? item.email || "Not Provided"
                    : item.Order?.Request?.Part?.name ??
                      item.Order?.Request?.custom_part_type ??
                      "Not Provided"}
                </TableCell> */}
                <TableCell className="capitalize py-3 px-2">
                  {statusFilter !== "disputes"
                    ? item.email || "Not Provided"
                    : item.User?.email || "Not Provided"}
                </TableCell>
                <TableCell className="capitalize py-3 px-2">
                  {statusFilter !== "disputes"
                    ? item?.phone || "Not Provided"
                    : item.User?.phone || "Not Provided"}
                </TableCell>

                {/* 
                <TableCell className="capitalize py-3 px-2">
                  {statusFilter === "SUPPLIER"
                    ? item.business_name || "Not Provided"
                    : item.User?.business_name || "Not Provided"}
                </TableCell> */}
                <TableCell className="capitalize py-3 px-2">
                  {statusFilter === "disputes"
                    ? item.User?.business_name ||
                      item.User?.company_name ||
                      "Not Provided"
                    : item.business_name || item.company_name || "Not Provided"}
                </TableCell>

                <TableCell className="capitalize py-3 px-2">
                  {item.createdAt
                    ? new Date(item.createdAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "Not Provided"}
                </TableCell>
              </TableRow>
            );
          }}
        </TableBody>
      </Table>
    </div>
  );
};

export default Users;
